import { MobileTimeIcon } from '@/components/Icons';
import useGeneratorStore from '@/state/generator-store';
import { ChevronLeft } from 'lucide-react';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

interface MobileDurationInputProps {
  setShowMainType: Dispatch<SetStateAction<'prompt' | 'duration'>>;
}

const MobileDurationInput: React.FC<MobileDurationInputProps> = ({ setShowMainType }) => {
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');
  const [focusedField, setFocusedField] = useState<'minutes' | 'seconds' | null>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  const handleMinutesChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const lastTwoDigits = numericValue.slice(-2);
    if (numericValue === '') {
      setMinutes('00');
      return;
    }

    const numValue = parseInt(lastTwoDigits);
    if (numValue >= 0 && numValue <= 99) {
      setMinutes(lastTwoDigits.padStart(2, '0'));
    }
  };

  const handleSecondsChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const lastTwoDigits = numericValue.slice(-2);
    if (numericValue === '') {
      setSeconds('00');
      return;
    }

    const numValue = parseInt(lastTwoDigits);
    if (numValue >= 0 && numValue <= 59) {
      setSeconds(lastTwoDigits.padStart(2, '0'));
    }
  };

  const { control, formValues, generatorForm } = useGeneratorStore();

  const {
    field: { value: durationValue, onChange },
  } = useController({
    name: 'duration',
    control,
  });

  useEffect(() => {
    const mins = Math.floor(durationValue / 60);
    const secs = durationValue % 60;
    setMinutes(mins.toString().padStart(2, '0'));
    setSeconds(secs.toString().padStart(2, '0'));
  }, [durationValue]);

  const handleApply = () => {
    const newTotal = Number(minutes) * 60 + Number(seconds);
    generatorForm?.setValue('duration', newTotal, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setShowMainType('prompt');
  };

  useEffect(() => {
    const updateCursorPosition = () => {
      if (focusedField === 'minutes' && minutesRef.current) {
        const rect = minutesRef.current.getBoundingClientRect();
        setCursorPosition(rect.left + rect.width / 2);
      } else if (focusedField === 'seconds' && secondsRef.current) {
        const rect = secondsRef.current.getBoundingClientRect();
        setCursorPosition(rect.left + rect.width / 2);
      } else {
        setCursorPosition(null);
      }
    };

    updateCursorPosition();
    window.addEventListener('resize', updateCursorPosition);
    return () => window.removeEventListener('resize', updateCursorPosition);
  }, [focusedField]);

  const handleMinutesFocus = () => {
    setFocusedField('minutes');
    if (minutesRef.current) {
      minutesRef.current.select();
    }
  };

  const handleSecondsFocus = () => {
    setFocusedField('seconds');
    if (secondsRef.current) {
      secondsRef.current.select();
    }
  };

  return (
    <div
      className="bg-[#1C1C1E] text-white rounded-lg"
      style={{
        background: 'linear-gradient(#191A1B, #1E2021)',
        border: '1px solid #474A4C',
        boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
      }}
    >
      <div className="flex items-center px-4 py-3">
        <button className="p-1" onClick={() => setShowMainType('prompt')}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="flex-1 text-center text-[17px] font-medium">
          <span className="inline-flex items-center gap-1">
            <MobileTimeIcon />
            Track Duration
          </span>
        </h1>
      </div>

      <div className="flex justify-center items-center py-2">
        <div className="relative flex items-center text-[64px] font-light tracking-tight text-gray-600">
          <input
            ref={minutesRef}
            type="text"
            inputMode="numeric"
            value={minutes}
            onChange={(e) => handleMinutesChange(e.target.value)}
            onFocus={handleMinutesFocus}
            onBlur={() => setFocusedField(null)}
            onContextMenu={(e) => e.preventDefault()}
            className="w-[76px] bg-transparent text-center focus:outline-none"
          />
          <span>:</span>
          <input
            ref={secondsRef}
            type="text"
            inputMode="numeric"
            value={seconds}
            onChange={(e) => handleSecondsChange(e.target.value)}
            onFocus={handleSecondsFocus}
            onBlur={() => setFocusedField(null)}
            onContextMenu={(e) => e.preventDefault()}
            className="w-[76px] bg-transparent text-center focus:outline-none"
          />
        </div>
      </div>

      <div className="w-[95%] m-auto h-px bg-[#474A4C]" />

      <button type="button" onClick={handleApply} className="w-full px-4 py-3 mt-2 text-jen-orange text-[17px] font-medium text-center">
        Apply Duration
      </button>
    </div>
  );
};

export default MobileDurationInput;
